import { useEffect, useState } from 'react';
import { browserTranslator } from '../i18n/translate';
import type { TextTranslator } from '../i18n/translate';

/*
 * ONE VERSE, TRANSLATED, as a small state machine.
 *
 * `idle` (source and target are the same, or there is nothing to translate), `translating`, `done`,
 * `unsupported` (this browser cannot, or the pairing is unavailable), or `error`. A module cache keys
 * on from|to|text so the same verse is never translated twice — text is stable, so the cache never
 * goes stale. The default translator is resolved ONCE at load (a stable reference, so the effect does
 * not re-run every render); a test passes its own fake in its place.
 */
export type TranslateStatus = 'idle' | 'translating' | 'done' | 'unsupported' | 'error';
export interface Translated {
  status: TranslateStatus;
  text?: string;
}

const cache = new Map<string, string>();
const cacheKey = (from: string, to: string, text: string): string => `${from}|${to}|${text}`;

const DEFAULT_TRANSLATOR = browserTranslator();

export function useTranslated(
  text: string,
  from: string,
  to: string,
  translator: TextTranslator | null = DEFAULT_TRANSLATOR,
): Translated {
  const [state, setState] = useState<Translated>({ status: 'idle' });

  useEffect(() => {
    if (from === to || text.length === 0) { setState({ status: 'idle' }); return; }
    const hit = cache.get(cacheKey(from, to, text));
    if (hit !== undefined) { setState({ status: 'done', text: hit }); return; }
    if (!translator) { setState({ status: 'unsupported' }); return; }

    let live = true;
    setState({ status: 'translating' });
    void (async () => {
      try {
        if ((await translator.availability(from, to)) === 'no') {
          if (live) setState({ status: 'unsupported' });
          return;
        }
        const out = await translator.translate(text, from, to);
        cache.set(cacheKey(from, to, text), out);
        if (live) setState({ status: 'done', text: out });
      } catch {
        if (live) setState({ status: 'error' });
      }
    })();
    return () => { live = false; };
  }, [text, from, to, translator]);

  return state;
}
